import { Box, MenuItem, Select, SelectChangeEvent } from '@mui/material'
import React from 'react'
import {
	BaseDataAddress,
	BaseDataColumns,
	BaseDataContact,
	BaseDataHeader,
	BaseDataInput,
	BaseDataRow,
	BaseDataRows,
} from './BaseDataTable'
import { useAutoSave } from './SaveStateIndicator'
import updateRestaurantBaseData from '../mutations/updateRestaurantBaseData'
import upsertCompany from '../mutations/upsertCompany'
import {
	EDITABLE_COMPANY_FIELDS,
	EditableCompanyField,
	findLegalForm,
	LEGAL_FORM_ITEMS,
} from '../utils/company'
import { handleMutationError } from '../utils/errors'
import { Restaurant, RESTAURANT_FIELDS } from '../utils/restaurant'

type CompanyValues = Partial<Record<EditableCompanyField, string>>

const ADDRESS_AND_CONTACT_KEYS: string[] = [
	'street',
	'houseNumber',
	'zipCode',
	'city',
	'phone',
	'email',
]

// Die eigenen Firmenfelder vor Adresse und Kontakt, in der Reihenfolge von COMPANY_FIELDS; die
// Rechtsform steht als Dropdown davor.
const COMPANY_OWN_FIELDS = EDITABLE_COMPANY_FIELDS.filter(
	(field) => field.key !== 'legalForm' && !ADDRESS_AND_CONTACT_KEYS.includes(field.key)
)

const LEGAL_FORM_HINT = EDITABLE_COMPANY_FIELDS.find((field) => field.key === 'legalForm')?.hint

const RESTAURANT_FIELDS_BY_KEY = Object.fromEntries(
	RESTAURANT_FIELDS.map((field) => [field.key, field])
)

const sxBands = { display: 'flex', flexDirection: 'column', gap: '0.75rem' } as const

// Firma links, Restaurant rechts; Adresse und Kontakt stehen auf derselben Höhe. Jede Änderung
// wird kurz danach gespeichert: die Firma per upsertCompany (die erste Angabe legt sie an), das
// Restaurant per updateRestaurantBaseData, das leere Pflichtfelder stehen lässt.
const EditBaseDataForm = ({ restaurant }: { restaurant: Restaurant }): JSX.Element => {
	const company = restaurant.company ?? null
	const [companyValues, setCompanyValues] = React.useState<CompanyValues>(() =>
		Object.fromEntries(
			EDITABLE_COMPANY_FIELDS.map((field) => [field.key, String(company?.[field.key] ?? '')])
		)
	)
	const [restaurantValues, setRestaurantValues] = React.useState<Record<string, string>>(() =>
		Object.fromEntries(
			RESTAURANT_FIELDS.map((field) => [field.key, String(restaurant[field.key] ?? '')])
		)
	)

	const handleCompanyChange = React.useCallback((value: string | number, id?: string) => {
		if (id) setCompanyValues((current) => ({ ...current, [id]: String(value) }))
	}, [])
	const handleLegalFormChange = React.useCallback((event: SelectChangeEvent<string>) => {
		setCompanyValues((current) => ({ ...current, legalForm: event.target.value }))
	}, [])
	const handleRestaurantChange = React.useCallback((value: string | number, id?: string) => {
		if (id) setRestaurantValues((current) => ({ ...current, [id]: String(value) }))
	}, [])

	// Ohne Rechtsform steht noch nicht fest, welche Firmenfelder es gibt — dann bleibt es beim Dropdown.
	const legalForm = findLegalForm(companyValues.legalForm)
	const companyOwnFields = React.useMemo(
		() =>
			COMPANY_OWN_FIELDS.filter(
				(field) => field.key !== 'tradeRegisterNumber' || legalForm?.hasRegister
			),
		[legalForm]
	)

	const saveCompany = React.useCallback(
		async (values: CompanyValues) => {
			const savedLegalForm = findLegalForm(values.legalForm)
			const res = await upsertCompany({
				restaurantId: restaurant.id,
				input: Object.fromEntries(
					EDITABLE_COMPANY_FIELDS.map((field) =>
						field.key === 'tradeRegisterNumber' && savedLegalForm && !savedLegalForm.hasRegister
							? [field.key, null]
							: [field.key, values[field.key]?.trim() || null]
					)
				),
			})
			if (!res.success) handleMutationError('upsertCompany', res.errors)
			return res.success
		},
		[restaurant.id]
	)
	const saveRestaurant = React.useCallback(
		async (values: Record<string, string>) => {
			const res = await updateRestaurantBaseData({
				restaurantId: restaurant.id,
				input: Object.fromEntries(
					RESTAURANT_FIELDS.map((field) => [field.key, values[field.key]?.trim() || null])
				),
			})
			if (!res.success) handleMutationError('updateRestaurantBaseData', res.errors)
			return res.success
		},
		[restaurant.id]
	)
	const companySaveState = useAutoSave(companyValues, saveCompany)
	const restaurantSaveState = useAutoSave(restaurantValues, saveRestaurant)

	return (
		<Box sx={sxBands}>
			<BaseDataColumns>
				<BaseDataHeader
					title="Firma"
					saveState={companySaveState}
					note={
						[
							company ? null : 'Noch keine Firma zugeordnet — die erste Angabe legt sie an.',
							legalForm
								? null
								: 'Zuerst die Rechtsform wählen, davon hängen die weiteren Felder ab.',
						]
							.filter(Boolean)
							.join(' ') || undefined
					}
				/>
				<BaseDataHeader title="Restaurant" saveState={restaurantSaveState} />
			</BaseDataColumns>
			<BaseDataColumns>
				<BaseDataRows>
					<BaseDataRow label="Rechtsform" hint={LEGAL_FORM_HINT}>
						<Select
							variant="standard"
							size="small"
							fullWidth
							value={companyValues.legalForm ?? ''}
							onChange={handleLegalFormChange}
						>
							{LEGAL_FORM_ITEMS.map((item) => (
								<MenuItem key={item.value} value={item.value}>
									{item.label}
								</MenuItem>
							))}
						</Select>
					</BaseDataRow>
					{legalForm
						? companyOwnFields.map((field) => (
								<BaseDataRow
									key={field.key}
									label={
										field.key === 'managingDirector' ? legalForm.representativeLabel : field.label
									}
									hint={field.hint}
								>
									<BaseDataInput
										field={field}
										values={companyValues}
										onChange={handleCompanyChange}
									/>
								</BaseDataRow>
						  ))
						: null}
				</BaseDataRows>
				<BaseDataRows>
					<BaseDataRow
						label={RESTAURANT_FIELDS_BY_KEY.title.label}
						hint={RESTAURANT_FIELDS_BY_KEY.title.hint}
					>
						<BaseDataInput
							field={RESTAURANT_FIELDS_BY_KEY.title}
							values={restaurantValues}
							onChange={handleRestaurantChange}
						/>
					</BaseDataRow>
					<BaseDataRow
						label="Koordinaten"
						hint={RESTAURANT_FIELDS_BY_KEY.lat.hint}
						columns="1fr 1fr"
					>
						<BaseDataInput
							field={RESTAURANT_FIELDS_BY_KEY.lat}
							values={restaurantValues}
							onChange={handleRestaurantChange}
							placeHolder="Breite"
						/>
						<BaseDataInput
							field={RESTAURANT_FIELDS_BY_KEY.lon}
							values={restaurantValues}
							onChange={handleRestaurantChange}
							placeHolder="Länge"
						/>
					</BaseDataRow>
				</BaseDataRows>
			</BaseDataColumns>
			<BaseDataColumns>
				{legalForm ? (
					<BaseDataAddress
						fields={EDITABLE_COMPANY_FIELDS}
						values={companyValues}
						onChange={handleCompanyChange}
					/>
				) : (
					<Box />
				)}
				<BaseDataAddress
					fields={RESTAURANT_FIELDS}
					values={restaurantValues}
					onChange={handleRestaurantChange}
				/>
			</BaseDataColumns>
			<BaseDataColumns>
				{legalForm ? (
					<BaseDataContact
						fields={EDITABLE_COMPANY_FIELDS}
						values={companyValues}
						onChange={handleCompanyChange}
					/>
				) : (
					<Box />
				)}
				<BaseDataContact
					fields={RESTAURANT_FIELDS}
					values={restaurantValues}
					onChange={handleRestaurantChange}
				/>
			</BaseDataColumns>
		</Box>
	)
}

export default React.memo(EditBaseDataForm)
